import type { ContactPhotoResponse } from '../../../../../services/marketplace/generated/kubb/models/ContactPhotoResponse';
import { PHOTO_SLOTS } from './constants';

export type PhotoEntry = {
  slotId: string;
  photoId: string;
  previewUrl: string;
  uploadedAt: number;
};

export const getPhotoTimestamp = (photo: Pick<ContactPhotoResponse, 'createdAt'>): number => {
  const parsedUploadedAt = Date.parse(photo.createdAt);
  return Number.isNaN(parsedUploadedAt) ? 0 : parsedUploadedAt;
};

const toPhotoEntry = (slotId: string, photo: ContactPhotoResponse): PhotoEntry => {
  return {
    slotId,
    photoId: photo.id,
    previewUrl: photo.url ?? '',
    uploadedAt: getPhotoTimestamp(photo),
  };
};

export const buildPhotoSlotMap = (
  apiPhotos: readonly ContactPhotoResponse[],
): Map<string, PhotoEntry> => {
  const mapped = new Map<string, PhotoEntry>();
  const unlabeled: ContactPhotoResponse[] = [];
  const slotIds = new Set(PHOTO_SLOTS.map((slot) => slot.id));

  apiPhotos.forEach((photo) => {
    if (!photo.url) return;
    const label = photo.label ?? '';
    if (slotIds.has(label)) {
      const nextEntry = toPhotoEntry(label, photo);
      const currentEntry = mapped.get(label);
      if (!currentEntry || nextEntry.uploadedAt >= currentEntry.uploadedAt) {
        mapped.set(label, nextEntry);
      }
      return;
    }
    if (photo.photoType === 'standard') {
      unlabeled.push(photo);
    }
  });

  const remainingSlotIds = PHOTO_SLOTS.map((slot) => slot.id).filter(
    (slotId) => !mapped.has(slotId),
  );

  const sortedUnlabeledPhotos = unlabeled.sort(
    (a, b) => getPhotoTimestamp(b) - getPhotoTimestamp(a),
  );

  sortedUnlabeledPhotos.forEach((photo, index) => {
    const slotId = remainingSlotIds[index];
    if (!slotId) return;
    mapped.set(slotId, toPhotoEntry(slotId, photo));
  });

  return mapped;
};
